import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

import { supabase } from 'lib/supabase';
import { format } from 'date-fns';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ status: newStatus })
        ?.eq('id', userId);

      if (error) throw error;

      // Log action
      await supabase?.rpc('log_user_action', {
        p_user_id: (await supabase?.auth?.getUser())?.data?.user?.id,
        p_action: `user_status_changed_to_${newStatus}`,
        p_resource_type: 'user',
        p_resource_id: userId,
        p_severity: 'warning'
      });

      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error?.message);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ role: newRole })
        ?.eq('id', userId);

      if (error) throw error;

      // Log action
      await supabase?.rpc('log_user_action', {
        p_user_id: (await supabase?.auth?.getUser())?.data?.user?.id,
        p_action: `user_role_changed_to_${newRole}`,
        p_resource_type: 'user',
        p_resource_id: userId,
        p_severity: 'critical'
      });

      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error?.message);
    }
  };

  const filteredUsers = users?.filter((user) => {
    const matchesSearch = user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user?.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success-100', text: 'text-success-700', label: 'Active' },
      inactive: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Inactive' },
      suspended: { bg: 'bg-error-100', text: 'text-error-700', label: 'Suspended' },
      pending: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Pending' }
    };
    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      client: { bg: 'bg-primary-100', text: 'text-primary-700', label: 'Client' },
      lawyer: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Lawyer' },
      partner: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Partner' },
      admin: { bg: 'bg-error-100', text: 'text-error-700', label: 'Admin' }
    };
    const config = roleConfig?.[role] || roleConfig?.client;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary-100 rounded w-1/4"></div>
          <div className="h-64 bg-secondary-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-card">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading font-bold text-text-primary">User Management</h2>
            <p className="text-sm text-text-secondary mt-1">
              Manage user accounts, roles, and permissions
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
              <Icon name="UserPlus" size={16} />
              <span>Add User</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Roles</option>
            <option value="client">Client</option>
            <option value="lawyer">Lawyer</option>
            <option value="partner">Partner</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {filteredUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-secondary-50 transition-smooth">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{user?.full_name}</div>
                      <div className="text-sm text-text-secondary">{user?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {getRoleBadge(user?.role)}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(user?.status)}
                </td>
                <td className="px-6 py-4 text-sm text-text-secondary">
                  {format(new Date(user?.created_at), 'MMM dd, yyyy')}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    {user?.status === 'pending' && (
                      <button
                        onClick={() => handleStatusChange(user?.id, 'active')}
                        className="p-2 text-success hover:bg-success-50 rounded-lg transition-smooth"
                        title="Approve User"
                      >
                        <Icon name="Check" size={16} />
                      </button>
                    )}
                    {user?.status === 'active' && (
                      <button
                        onClick={() => handleStatusChange(user?.id, 'suspended')}
                        className="p-2 text-error hover:bg-error-50 rounded-lg transition-smooth"
                        title="Suspend User"
                      >
                        <Icon name="Ban" size={16} />
                      </button>
                    )}
                    {user?.status === 'suspended' && (
                      <button
                        onClick={() => handleStatusChange(user?.id, 'active')}
                        className="p-2 text-success hover:bg-success-50 rounded-lg transition-smooth"
                        title="Reactivate User"
                      >
                        <Icon name="CheckCircle" size={16} />
                      </button>
                    )}
                    <button
                      className="p-2 text-primary hover:bg-primary-50 rounded-lg transition-smooth"
                      title="Edit User"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">No users found matching your filters</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;